import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ShoppingCart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { getProxiedImageUrl, getProductPath } from '../utils/helpers';

const ProductCarousel = ({ products, title, subtitle, itemsPerView = 5 }) => {
  const { language } = useLanguage();
  const { addToCart } = useCart();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responsiveItemsPerView, setResponsiveItemsPerView] = useState(itemsPerView);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        // Mobile: 2 prodotti affiancati
        setResponsiveItemsPerView(2);
      } else if (window.innerWidth < 1024) {
        // Tablet: 2 prodotti
        setResponsiveItemsPerView(2);
      } else if (window.innerWidth < 1280) {
        // Desktop piccolo: 3 prodotti
        setResponsiveItemsPerView(3);
      } else {
        // Desktop grande: 5 prodotti
        setResponsiveItemsPerView(itemsPerView);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [itemsPerView]);

  const maxIndex = Math.max(0, products.length - responsiveItemsPerView);

  const next = () => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
  };

  const prev = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  // Touch swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < maxIndex) {
      next();
    }
    if (isRightSwipe && currentIndex > 0) {
      prev();
    }
  };

  if (!products || products.length === 0) {
    return null; // Hide section if no products
  }

  return (
    <section className="py-20 px-6 lg:px-[7.6923%]" style={{ backgroundColor: 'var(--bg-secondary)' }}>
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="display-medium mb-4">{title}</h2>
          {subtitle && (
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {subtitle}
            </p>
          )}
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows */}
          {products.length > responsiveItemsPerView && (
            <>
              <button
                onClick={prev}
                disabled={currentIndex === 0}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110"
                style={{ 
                  backgroundColor: 'var(--brand-primary)',
                  color: '#000'
                }}
              >
                <ChevronLeft size={24} className="md:w-7 md:h-7" />
              </button>

              <button
                onClick={next}
                disabled={currentIndex >= maxIndex}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110"
                style={{ 
                  backgroundColor: 'var(--brand-primary)',
                  color: '#000'
                }}
              >
                <ChevronRight size={24} className="md:w-7 md:h-7" />
              </button>
            </>
          )}

          {/* Products Container */}
          <div 
            className="overflow-hidden -mx-2"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / responsiveItemsPerView)}%)`
              }}
            >
              {products.map((product) => (
                <div 
                  key={product.sku} 
                  className="flex-shrink-0 px-2"
                  style={{
                    width: `${100 / responsiveItemsPerView}%`
                  }}
                >
                  <div className="dark-card overflow-hidden">
                  <Link to={getProductPath(product)}>
                    <div className="aspect-square overflow-hidden bg-white relative">
                      {/* Badge ESAURITO */}
                      {product.stock === 0 && (
                        <div className="absolute top-4 right-4 z-10 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                          {language === 'it' ? 'ESAURITO' : 'OUT OF STOCK'}
                        </div>
                      )}
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={getProxiedImageUrl(product.images[0])}
                          alt={language === 'it' ? product.name_it : product.name_en}
                          className={`w-full h-full object-contain p-4 dark-transition hover:scale-110 ${product.stock === 0 ? 'opacity-60' : ''}`}
                          loading="lazy"
                          style={{ backgroundColor: '#ffffff' }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20">
                          {language === 'it' ? 'Immagine non disponibile' : 'No Image'}
                        </div>
                      )}
                    </div>
                  </Link>

                  <div className="p-3 md:p-6">
                    <div className="text-xs md:text-sm mb-2" style={{ color: 'var(--brand-primary)' }}>
                      {product.category}
                    </div>
                    <Link to={getProductPath(product)}>
                      <h3 className="text-base md:text-xl font-semibold mb-2 md:mb-3 hover:text-primary transition-colors line-clamp-2">
                        {language === 'it' ? product.name_it : product.name_en}
                      </h3>
                    </Link>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3 md:mb-4">
                      <div className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--brand-primary)' }}>
                        €{product.price ? product.price.toFixed(2) : '0.00'}
                      </div>
                      {product.stock > 0 ? (
                        <div className="text-xs md:text-sm px-2 md:px-3 py-1.5 md:py-1 bg-green-500/30 md:bg-green-500/20 text-green-400 font-semibold rounded whitespace-nowrap">
                          {language === 'it' ? 'Disponibile' : 'In Stock'}
                        </div>
                      ) : (
                        <div className="text-xs md:text-sm px-2 md:px-3 py-1.5 md:py-1 bg-red-500/30 md:bg-red-500/20 text-red-400 font-semibold rounded whitespace-nowrap">
                          {language === 'it' ? 'Esaurito' : 'Out of Stock'}
                        </div>
                      )}
                    </div>

                    {/* Add to Cart Button */}
                    <button 
                      className={`w-full py-2 md:py-3 px-3 md:px-4 rounded font-semibold transition-all flex items-center justify-center gap-2 text-sm md:text-base ${
                        product.stock === 0 
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                          : 'btn-primary'
                      }`}
                      disabled={product.stock === 0}
                      onClick={(e) => {
                        e.preventDefault();
                        if (product.stock > 0) {
                          addToCart(product);
                        }
                      }}
                    >
                      <ShoppingCart size={18} className="md:w-5 md:h-5" />
                      <span className="hidden sm:inline">
                        {product.stock === 0 
                          ? (language === 'it' ? 'Non Disponibile' : 'Unavailable')
                          : (language === 'it' ? 'Aggiungi al Carrello' : 'Add to Cart')
                        }
                      </span>
                      <span className="sm:hidden">
                        {product.stock === 0 
                          ? (language === 'it' ? 'Esaurito' : 'N/A')
                          : (language === 'it' ? 'Aggiungi' : 'Add')
                        }
                      </span>
                    </button>
                  </div>
                </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots Indicator */}
          {products.length > responsiveItemsPerView && (
            <div className="flex justify-center gap-2 mt-6 md:mt-8">
              {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
                <button
                  key={`dot-${idx}`}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all ${
                    currentIndex === idx 
                      ? 'w-6 md:w-8' 
                      : 'opacity-50 hover:opacity-75'
                  }`}
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProductCarousel;
